import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ControlDocsService, ShareLinkAccessResponse, ShareLinkFileInfo } from '../../services/controldocs.service';
import { API_BASE_URL } from '../../services/api-base';

@Component({
  selector: 'app-controldocs-share',
  templateUrl: './controldocs-share.component.html',
  styleUrls: ['./controldocs-share.component.scss']
})
export class ControldocsShareComponent implements OnInit {
  token = '';
  password = '';
  searchTerm = '';
  state: 'prompt' | 'loading' | 'content' | 'error' = 'prompt';
  errorMessage = '';
  data: ShareLinkAccessResponse | null = null;
  apiBaseUrl = `${API_BASE_URL}`;
  breadcrumbs: { id: number; name: string }[] = [];
  currentFolderId: number | null = null;
  lastPassword?: string;
  folderLoading = false;

  constructor(private route: ActivatedRoute, private docs: ControlDocsService) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.token = params.get('token') || '';
    });
  }

  submit() {
    if (!this.token) {
      this.errorMessage = 'Link inválido.';
      return;
    }
    this.state = 'loading';
    this.errorMessage = '';
    this.lastPassword = this.password || undefined;
    this.docs.accessShareLink(this.token, this.lastPassword).subscribe({
      next: (response) => {
        this.handleResponse(response);
        if (response.type === 'FILE' && response.file) {
          const url = this.fileUrl(response.file);
          if (url) {
            setTimeout(() => window.open(url, '_blank', 'noopener'), 200);
          }
        }
      },
      error: (err) => {
        this.state = 'prompt';
        if (err.status === 403) {
          const code = err.error?.error;
          if (code === 'PASSWORD_REQUIRED') {
            this.errorMessage = 'Informe a senha para acessar.';
          } else if (code === 'INVALID_PASSWORD') {
            this.errorMessage = 'Senha incorreta, tente novamente.';
          } else if (code === 'LINK_EXPIRED') {
            this.errorMessage = 'Este link expirou.';
            this.state = 'error';
          } else {
            this.errorMessage = 'Acesso negado.';
          }
        } else if (err.status === 404) {
          this.errorMessage = 'Link não encontrado ou removido.';
          this.state = 'error';
        } else {
          this.errorMessage = 'Não foi possível abrir o link.';
        }
      }
    });
  }

  reset() {
    this.state = 'prompt';
    this.password = '';
    this.searchTerm = '';
    this.data = null;
    this.errorMessage = '';
    this.breadcrumbs = [];
    this.currentFolderId = null;
    this.folderLoading = false;
    this.lastPassword = undefined;
  }

  fileUrl(file: ShareLinkFileInfo) {
    if (!file.url) {
      return '#';
    }
    return file.url.startsWith('http') ? file.url : `${this.apiBaseUrl}${file.url}`;
  }

  openSharedFile(file: ShareLinkFileInfo, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    const url = this.fileUrl(file);
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  hasSubfolders() {
    return this.filteredSubfolders.length > 0;
  }

  hasFiles() {
    return this.filteredFiles.length > 0;
  }

  get filteredSubfolders() {
    const term = this.searchTerm.trim().toLowerCase();
    const folders = this.data?.subfolders || [];
    if (!term) {
      return folders;
    }
    return folders.filter((folder) => String(folder.name || '').toLowerCase().includes(term));
  }

  get filteredFiles() {
    const term = this.searchTerm.trim().toLowerCase();
    const files = this.data?.files || [];
    if (!term) {
      return files;
    }
    return files.filter((file) => {
      const name = String(file.name || '').toLowerCase();
      const type = String(file.file_type || '').toLowerCase();
      return name.includes(term) || type.includes(term);
    });
  }

  get totalVisibleItems() {
    return this.filteredSubfolders.length + this.filteredFiles.length;
  }

  folderTypeLabel(folder: { visibility?: 'PUBLIC' | 'PRIVATE' }) {
    return folder.visibility === 'PUBLIC' ? 'Pública' : 'Particular';
  }

  formatFileSize(size?: number) {
    const bytes = Number(size || 0);
    if (!bytes) {
      return 'Tamanho não informado';
    }
    const units = ['bytes', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const digits = value >= 10 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(digits)} ${units[unitIndex]}`;
  }

  openSharedFolder(folder: { id: number; name: string }, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!folder?.id || folder.id === this.currentFolderId) {
      return;
    }
    this.fetchFolder(folder.id);
  }

  navigateBreadcrumb(index: number) {
    if (index < 0 || index >= this.breadcrumbs.length - 1) {
      return;
    }
    const target = this.breadcrumbs[index];
    if (!target) {
      return;
    }
    this.fetchFolder(target.id);
  }

  private fetchFolder(folderId: number) {
    if (!this.token) {
      return;
    }
    this.folderLoading = true;
    this.docs.accessShareLink(this.token, this.lastPassword, folderId).subscribe({
      next: (response) => {
        this.folderLoading = false;
        this.handleResponse(response);
      },
      error: () => {
        this.folderLoading = false;
        this.errorMessage = 'Não foi possível abrir esta pasta.';
      }
    });
  }

  private handleResponse(response: ShareLinkAccessResponse) {
    this.data = response;
    if (response.type === 'FOLDER' && response.folder) {
      this.state = 'content';
      this.currentFolderId = response.folder.id;
      if (response.breadcrumb && response.breadcrumb.length) {
        this.breadcrumbs = response.breadcrumb;
      } else {
        this.breadcrumbs = [{ id: response.folder.id, name: response.folder.name }];
      }
    } else {
      this.currentFolderId = null;
      this.breadcrumbs = [];
      this.state = 'content';
    }
  }
}
